import type { EditorElement } from '../types';

interface Props {
  elements: EditorElement[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onToggleVisible: (id: string) => void;
  onToggleLock: (id: string) => void;
  onReorder: (id: string, dir: 'up' | 'down') => void;
}

const TYPE_ICON: Record<string, string> = {
  text: 'ri-text',
  button: 'ri-cursor-fill',
  shape: 'ri-square-line',
  image: 'ri-image-line',
};

export default function LayersPanel({
  elements,
  selectedId,
  onSelect,
  onDelete,
  onDuplicate,
  onToggleVisible,
  onToggleLock,
  onReorder,
}: Props) {
  // Reversed for visual stacking order (top elements last in array = shown first)
  const reversed = [...elements].reverse();

  return (
    <div className="w-full md:w-52 bg-stone-900 md:border-r border-stone-800 flex flex-col overflow-hidden shrink-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-800 shrink-0 flex items-center justify-between">
        <p className="text-xs font-bold text-stone-200 uppercase tracking-widest">Layers</p>
        <span className="text-xs text-stone-500">{elements.length}</span>
      </div>

      {/* Layer items */}
      <div className="flex-1 overflow-y-auto py-2">
        {elements.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <i className="ri-stack-line text-stone-700 text-2xl block mb-2" />
            <p className="text-xs text-stone-600">No elements yet</p>
            <p className="text-xs text-stone-700 mt-1">Add text or a button</p>
          </div>
        ) : (
          <div className="space-y-0.5 px-2">
            {reversed.map((el, reversedIdx) => {
              const isSelected = selectedId === el.id;
              const originalIdx = elements.length - 1 - reversedIdx;

              return (
                <div
                  key={el.id}
                  className={`group rounded-lg transition-colors ${
                    isSelected ? 'bg-orange-500/15 border border-orange-500/30' : 'border border-transparent hover:bg-stone-800'
                  }`}
                >
                  {/* Main row */}
                  <div
                    className="flex items-center gap-2 px-2 py-2 cursor-pointer"
                    onClick={() => onSelect(el.id)}
                  >
                    {/* Type icon */}
                    <div className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${
                      isSelected ? 'bg-orange-500/20 text-orange-400' : 'bg-stone-800 text-stone-500'
                    }`}>
                      <i className={`${TYPE_ICON[el.type] ?? 'ri-image-line'} text-xs`} />
                    </div>

                    {/* Label */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${isSelected ? 'text-orange-300' : 'text-stone-400'}`}>
                        {el.type === 'shape' ? 'Shape' : el.type === 'image' ? 'Logo / Image' : el.content.slice(0, 18) || `${el.type}`}
                      </p>
                      <p className="text-xs text-stone-600 capitalize">{el.type === 'image' ? 'Image' : el.type}</p>
                    </div>

                    {/* Visibility toggle */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleVisible(el.id); }}
                      title={el.visible ? 'Hide element' : 'Show element'}
                      className={`w-5 h-5 flex items-center justify-center rounded transition-colors cursor-pointer ${
                        el.visible ? 'text-stone-500 hover:text-stone-300' : 'text-stone-700 hover:text-stone-500'
                      }`}
                    >
                      <i className={el.visible ? 'ri-eye-line text-xs' : 'ri-eye-off-line text-xs'} />
                    </button>
                  </div>

                  {/* Expanded actions when selected */}
                  {isSelected && (
                    <div className="flex items-center justify-between px-2 pb-2 gap-1">
                      {/* Reorder */}
                      <div className="flex gap-1">
                        <button
                          onClick={() => onReorder(el.id, 'up')}
                          disabled={originalIdx === elements.length - 1}
                          title="Bring forward"
                          className={`w-6 h-6 flex items-center justify-center rounded text-xs transition-colors cursor-pointer ${
                            originalIdx < elements.length - 1
                              ? 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'
                              : 'bg-stone-800/50 text-stone-700 cursor-not-allowed'
                          }`}
                        >
                          <i className="ri-bring-forward text-xs" />
                        </button>
                        <button
                          onClick={() => onReorder(el.id, 'down')}
                          disabled={originalIdx === 0}
                          title="Send backward"
                          className={`w-6 h-6 flex items-center justify-center rounded text-xs transition-colors cursor-pointer ${
                            originalIdx > 0
                              ? 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'
                              : 'bg-stone-800/50 text-stone-700 cursor-not-allowed'
                          }`}
                        >
                          <i className="ri-send-backward text-xs" />
                        </button>
                      </div>

                      <div className="flex gap-1">
                        {/* Lock */}
                        <button
                          onClick={() => onToggleLock(el.id)}
                          title={el.locked ? 'Unlock' : 'Lock'}
                          className={`w-6 h-6 flex items-center justify-center rounded text-xs transition-colors cursor-pointer ${
                            el.locked
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-stone-800 text-stone-500 hover:bg-stone-700 hover:text-stone-300'
                          }`}
                        >
                          <i className={el.locked ? 'ri-lock-fill text-xs' : 'ri-lock-unlock-line text-xs'} />
                        </button>

                        {/* Duplicate */}
                        <button
                          onClick={() => onDuplicate(el.id)}
                          title="Duplicate"
                          className="w-6 h-6 flex items-center justify-center rounded bg-stone-800 text-stone-500 hover:bg-stone-700 hover:text-stone-300 transition-colors cursor-pointer"
                        >
                          <i className="ri-file-copy-line text-xs" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => onDelete(el.id)}
                          title="Delete"
                          className="w-6 h-6 flex items-center justify-center rounded bg-stone-800 text-red-500/70 hover:bg-red-900/30 hover:text-red-400 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-stone-800 shrink-0">
        <div className="space-y-1 text-xs text-stone-600">
          <div className="flex items-center gap-2">
            <i className="ri-arrow-up-line text-xs" />
            <span>Top = front layer</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-information-line text-xs" />
            <span>Select to reorder</span>
          </div>
        </div>
      </div>
    </div>
  );
}
